import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productsApi, categoriesApi } from '../api';
import { useCartStore } from '../store';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Search, ShoppingCart, Package, Loader2 } from 'lucide-react';
import ProductImage from '../components/ProductImage';

const formatMoney = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount);
};

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);
  const { addToCart } = useCartStore();

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [activeCategory, keyword]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await productsApi.getAll({
        category_id: activeCategory,
        keyword: keyword || undefined,
      });
      setProducts(response.data.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    setAddingToCart(productId);
    const result = await addToCart(productId);
    if (result.success) {
      toast.success('Added to cart!');
    } else {
      toast.error(result.error);
    }
    setAddingToCart(null);
  };

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#FFD700]/20 to-transparent border border-[#FFD700]/30 rounded-xl p-6">
        <h1 className="text-2xl font-bold text-white font-['Outfit'] mb-2">
          Welcome to CrackBox
        </h1>
        <p className="text-zinc-400">
          The best place to get digital products at affordable prices.
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          placeholder="Search products..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="pl-10 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#FFD700]"
          data-testid="search-input"
        />
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={activeCategory === null ? 'default' : 'outline'}
          onClick={() => setActiveCategory(null)}
          className={activeCategory === null 
            ? 'bg-[#FFD700] text-black hover:bg-[#E5C100] font-black uppercase tracking-wider text-sm shadow-[0_0_15px_rgba(255,215,0,0.3)]' 
            : 'border-2 border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/20 font-black uppercase tracking-wider text-sm'
          }
          data-testid="category-all-btn"
        >
          All Products
        </Button>
        {categories.map((category) => (
          <Button
            key={category.id}
            variant={activeCategory === category.id ? 'default' : 'outline'}
            onClick={() => setActiveCategory(category.id)}
            className={activeCategory === category.id
              ? 'bg-[#FFD700] text-black hover:bg-[#E5C100] font-black uppercase tracking-wider text-sm shadow-[0_0_15px_rgba(255,215,0,0.3)]'
              : 'border-2 border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/20 font-black uppercase tracking-wider text-sm'
            }
            data-testid={`category-${category.id}-btn`}
          >
            {category.name}
          </Button>
        ))}
      </div>

      {/* Products Grid - 4 per row */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="bg-[#0A0A0A] border-white/10">
              <Skeleton className="h-28 w-full rounded-t-lg bg-white/5" />
              <CardContent className="p-3 space-y-2">
                <Skeleton className="h-4 w-3/4 bg-white/5" />
                <Skeleton className="h-3 w-1/2 bg-white/5" />
                <Skeleton className="h-7 w-full bg-white/5" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No products found</h3>
          <p className="text-zinc-400">Try adjusting your search or category filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
          {products.map((product) => (
            <Link 
              key={product.id} 
              to={`/products/${product.id}`}
              className="group"
              data-testid={`product-card-${product.id}`}
            >
              <Card className="bg-[#0A0A0A] border-2 border-[#FFD700]/30 hover:border-[#FFD700] transition-all duration-300 overflow-hidden h-full hover:shadow-[0_0_25px_rgba(255,215,0,0.3)] hover:-translate-y-1">
                <div className="relative h-28 bg-gradient-to-br from-[#121212] to-[#0A0A0A] overflow-hidden">
                  <ProductImage 
                    src={product.thumbnail} 
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                  {product.has_discount && (
                    <Badge className="absolute top-1.5 right-1.5 bg-red-600 text-white text-xs font-black px-1.5 py-0.5 shadow-lg z-10">
                      -{product.discount}%
                    </Badge>
                  )}
                  {product.category_name && (
                    <Badge className="absolute top-1.5 left-1.5 bg-black/80 text-[#FFD700] text-xs font-black uppercase tracking-wider px-1.5 py-0.5 backdrop-blur-sm border border-[#FFD700]/30 z-10">
                      {product.category_name}
                    </Badge>
                  )}
                </div>
                <CardContent className="p-3 space-y-2">
                  <h3 className="font-black text-white text-sm line-clamp-1 group-hover:text-[#FFD700] transition-colors uppercase tracking-wide">
                    {product.title}
                  </h3>
                  
                  <div className="flex items-center gap-1.5">
                    {product.has_discount && (
                      <span className="text-xs text-zinc-500 line-through font-medium">
                        {formatMoney(product.price)}
                      </span>
                    )}
                    <span className="text-lg font-black text-[#FFD700] drop-shadow-[0_0_10px_rgba(255,215,0,0.3)]">
                      {formatMoney(product.current_price)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-zinc-500 font-semibold">
                      <span className={`font-black ${product.stock > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {product.stock}
                      </span> in stock
                    </span>
                    <Button
                      size="sm"
                      disabled={product.stock === 0 || addingToCart === product.id}
                      onClick={(e) => handleAddToCart(e, product.id)}
                      className="bg-[#FFD700] text-black hover:bg-[#E5C100] h-7 px-2 text-xs font-black uppercase tracking-wide shadow-[0_0_10px_rgba(255,215,0,0.2)]"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      {addingToCart === product.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Add
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
